import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

/**
 * Mobiele navigatie (< lg). Een hamburger-knop in de header opent een paneel ONDER de
 * header met de nav-items onder elkaar, een scheidingslijn en daaronder `children`
 * (omgeving-kiezer, preview-knop, uitloggen). Sluit bij: klik op een nav-item, klik op de
 * achtergrond, of Escape (zelfde Escape-patroon als CelMenu in Weekrooster.jsx).
 *
 * Het paneel heeft de DONKERE balk-achtergrond (bg-brand-700), zodat de text-brand-100-
 * knoppen leesbaar blijven. Zichtbaarheid (< lg) regelt de aanroeper via een lg:hidden-
 * wrapper; het paneel positioneert zich met `top-full` t.o.v. het `relative` <header>.
 *
 * `badges` is hetzelfde object-per-route als bij NavBalk ({ '/ruilverzoeken': 3 }), zodat
 * de tellers in het hamburger-menu exact hetzelfde gedrag geven als in de brede balk.
 */
export default function MobielMenu({ navItems, badges = {}, children }) {
  const [open, setOpen] = useState(false);

  // Escape sluit — zelfde patroon als CelMenu in Weekrooster.jsx.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Menu sluiten' : 'Menu openen'}
        aria-expanded={open}
        className="flex items-center p-2 rounded-md text-brand-100 hover:text-white hover:bg-white/10 transition-colors"
      >
        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {open && (
        <>
          {/* Klik op de achtergrond sluit (zelfde idee als de overlay in CelMenu). */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Paneel onder de header, volledige breedte, donkere balk-achtergrond. */}
          <div className="absolute left-0 right-0 top-full z-50 bg-brand-700 border-t border-white/10 shadow-lg">
            <nav className="flex flex-col py-1">
              {navItems.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                      isActive ? 'text-white bg-white/10' : 'text-brand-100 hover:text-white hover:bg-white/10'
                    }`
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {label}
                  {badges[to] > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-semibold leading-none">
                      {badges[to] > 9 ? '9+' : badges[to]}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-white/10 px-4 py-3 flex flex-col items-stretch gap-3">
              {children}
            </div>
          </div>
        </>
      )}
    </>
  );
}
